import {
  IMeasurementType,
  IUnit,
  IUnit2,
  IUnit3,
  IUnit4,
  IUnit5,
  IUnit6,
  IUnitsInfo
} from './UnitModel';
import unitInfo from './UnitInfo.json';

class UnitUtility {
  private unitsInfo: IUnitsInfo[] = [];

  /**
   * To set unitsInfo when it changed
   * @param unitsInfo units info array from user account settings
   */
  setUnitsInfo(unitsInfo: IUnitsInfo[]) {
    this.unitsInfo = unitsInfo;
  }

  /**
   * To get meaurement object from unitInfo json file
   * @param unit unit name
   * @returns measurement object for given unit name
   */
  getMeasurementTypeObject = (unit: string) => {
    return unitInfo.unitdb.measurementType.find((measurementTypeObject) => {
      if (Array.isArray(measurementTypeObject.unit)) {
        return measurementTypeObject.unit.find(
          (unitObject) => unitObject._name === unit
        );
      } else {
        return measurementTypeObject._name === unit;
      }
    });
  };

  /**
   * To get unit object from measurement units
   * @param fromMeasurementTypeObject measurement object
   * @param unitName unit name
   * @returns unit object for given unit name
   */
  private getUnitObject = (
    fromMeasurementTypeObject: IMeasurementType,
    unitName: string
  ): IUnit | IUnit2 | IUnit3 | IUnit4 | IUnit5 | IUnit6 | undefined => {
    try {
      if (Array.isArray(fromMeasurementTypeObject.unit)) {
        const unit = fromMeasurementTypeObject.unit.find(
          (obj) => obj._name === unitName
        );
        if (unit) {
          return unit;
        }
      } else if (fromMeasurementTypeObject.unit._name === unitName) {
        return fromMeasurementTypeObject.unit;
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * To convert value from one unit to other unit
   * @param fromUnit convert unit from
   * @param toUnit convert unit to
   * @param value value to convert
   * @returns converted value
   */
  convertValue = (fromUnit: string, toUnit: string, value: number) => {
    if (value === undefined || value === null) {
      return value;
    }

    if (fromUnit === toUnit || !fromUnit || !toUnit) {
      return parseFloat(String(value));
    }

    const fromMeasurementTypeObject = this.getMeasurementTypeObject(fromUnit);
    const toMeasurementTypeObject = this.getMeasurementTypeObject(toUnit);

    if (
      (fromMeasurementTypeObject &&
        fromMeasurementTypeObject._name === 'miscellaneous') ||
      (fromMeasurementTypeObject &&
        toMeasurementTypeObject &&
        fromMeasurementTypeObject._name !== toMeasurementTypeObject._name)
    ) {
      return parseFloat(String(value));
    }
    if (fromMeasurementTypeObject) {
      const fromUnitObject = this.getUnitObject(
        fromMeasurementTypeObject,
        fromUnit
      );
      const fromUnitScale =
        fromUnitObject && '_scale' in fromUnitObject && fromUnitObject._scale
          ? Number(fromUnitObject._scale)
          : 1;
      const fromUnitOffset =
        fromUnitObject && '_offset' in fromUnitObject && fromUnitObject._offset
          ? Number(fromUnitObject._offset)
          : 0;
      const toUnitObject = this.getUnitObject(
        fromMeasurementTypeObject,
        toUnit
      );
      const toUnitScale =
        toUnitObject && '_scale' in toUnitObject && toUnitObject._scale
          ? Number(toUnitObject._scale)
          : 1;
      const toUnitOffset =
        toUnitObject && '_offset' in toUnitObject && toUnitObject._offset
          ? Number(toUnitObject._offset)
          : 0;

      const dimValue =
        parseFloat(String(value * fromUnitScale)) +
        parseFloat(String(fromUnitOffset));
      let convertedValue = parseFloat(String(dimValue));
      if (toUnitOffset) {
        convertedValue = convertedValue - toUnitOffset;
      }

      if (toUnitScale) {
        convertedValue = convertedValue / toUnitScale;
      }
      return convertedValue;
    }
  };

  /**
   * To get unit symbol from unit name
   * @param unitName unit name
   * @returns unit symbol
   */
  private getUnitSymbol = (unitName: string) => {
    if (!unitName) {
      return '';
    }

    if (unitName === 'liter') {
      return 'L';
    }
    const measurementTypeObject = this.getMeasurementTypeObject(unitName);
    if (measurementTypeObject) {
      const unitObject = this.getUnitObject(measurementTypeObject, unitName);
      if (unitObject) {
        return unitObject._symbol ? unitObject._symbol : '';
      } else {
        return '';
      }
    } else {
      return '';
    }
  };

  /**
   * To get measurement type for given unit name
   * @param unitName unit name
   * @returns mesurement type
   */
  private getMeasurementType = (unitName: string) => {
    if (!unitName) {
      return '';
    }
    const measurementTypeObject = this.getMeasurementTypeObject(unitName);
    if (measurementTypeObject) {
      return measurementTypeObject._name ? measurementTypeObject._name : '';
    } else {
      return '';
    }
  };

  /**
   * To get user preffered unit name, symbol and value
   * @param nativeUnitName
   * @param value value to convert in user preffered unit
   * @param precision precision
   * @returns user preffered unit name, unit symbol and value
   */
  getUserPreferredUnitValue = (
    nativeUnitName: string,
    value: string,
    precision?: number
  ): {
    unitName: string;
    unitSymbol: string;
    value: string;
  } => {
    if (!(this.unitsInfo && this.unitsInfo.length > 0)) {
      return {
        unitName: nativeUnitName,
        unitSymbol: this.getUnitSymbol(nativeUnitName),
        value: isNaN(Number(value))
          ? '-'
          : Number(value).toFixed(precision || 0)
      };
    }
    const measurementType = this.getMeasurementType(nativeUnitName);
    let preferredUnits = this.unitsInfo.filter(
      (x) => x.MeasurementType === measurementType
    );

    if (preferredUnits.length > 1) {
      preferredUnits = preferredUnits.filter(
        (preferredUnit) => preferredUnit.UnitName === nativeUnitName
      );
    }

    const preferredUnitObj = preferredUnits[0];

    const preferredUnitName = preferredUnitObj
      ? preferredUnitObj.UnitName
      : nativeUnitName;
    const preferredUnitSymbol = this.getUnitSymbol(preferredUnitName);
    const isNumeric = !isNaN(Number(value));
    let convertedValue = null;

    if (
      preferredUnitObj &&
      nativeUnitName !== preferredUnitName &&
      value &&
      isNumeric
    ) {
      convertedValue = this.convertValue(
        nativeUnitName,
        preferredUnitName,
        Number(value)
      );
      convertedValue =
        convertedValue &&
        convertedValue.toFixed(
          preferredUnitObj.unitPrecision || precision || 0
        );
    } else if (preferredUnitObj && value && isNumeric) {
      convertedValue = Number(value).toFixed(
        preferredUnitObj.unitPrecision || precision || 0
      );
    } else if (value && isNumeric) {
      convertedValue = Number(value).toFixed(precision || 0);
    } else if (value === null) {
      convertedValue = '-';
    }

    return {
      unitName: preferredUnitName,
      unitSymbol: preferredUnitName === 'liter' ? 'L' : preferredUnitSymbol,
      value: convertedValue || value
    };
  };
}
export default new UnitUtility();
